// ProposalDetailWeb.tsx
import React, { useMemo } from "react";
import { Button, Card, Space, Table, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { ProposalDetail } from "../model/proposalDetail.ui";
import { formatDate, formatDateDDMM } from "../../../utils/constants";
import { getStatusConfig } from "./proposalHelpers";

const { Title, Text } = Typography;

interface ProposalDetailDesktopProps {
    proposal?: ProposalDetail;
    onCreateOrder?: () => void;
    onExport?: () => void;
}

export const ProposalDetailDesktop: React.FC<ProposalDetailDesktopProps> = ({
    proposal,
    onCreateOrder,
    onExport
}) => {
    const scheduleDates = useMemo(() => {
        if (!proposal?.items) return [];
        const datesSet = new Set<string>();
        proposal.items.forEach(item => {
            item.schedules?.forEach(s => {
                if (s.delivery_date) datesSet.add(formatDate(s.delivery_date));
            });
        });
        return Array.from(datesSet).sort();
    }, [proposal]);

    const columns: ColumnsType<any> = useMemo(() => {
        const baseColumns: ColumnsType<any> = [
            {
                title: "商品名", // Sản phẩm
                dataIndex: "product_name",
                key: "product_name",
                render: (text) => <Text strong>{text || "-"}</Text>
            },
            {
                title: "産地", // Nơi SX
                dataIndex: "origin",
                key: "origin",
                render: (text) => text || "-"
            },
            {
                title: "仕入先", // Nhà CC
                dataIndex: "supplier_name",
                key: "supplier_name",
                render: (text) => <Text style={{ color: '#6B7280' }}>{text || "-"}</Text>
            },
            {
                title: "数量", // Khối lượng
                key: "total_quantity",
                render: (_, record) => {
                    const total = record.schedules?.reduce((acc: number, e: any) => acc + Number(e.quantity || 0), 0) || 0;
                    return total > 0 ? `${total}kg` : "-";
                }
            },
            {
                title: "等級", // Grade
                dataIndex: "grade",
                key: "grade",
                render: (text) => text || "-"
            },
            {
                title: "サイズ", // Size
                dataIndex: "size",
                key: "size",
                render: (text) => text || "-"
            },
            {
                title: "梱包", // Đóng gói
                dataIndex: "package",
                key: "package",
                render: (text) => text ? <Text style={{ color: '#2563EB' }}>{text}</Text> : "-"
            },
            {
                title: "入数", // Item/pack
                dataIndex: "item_per_pack",
                key: "item_per_pack",
                render: (text) => text || "-"
            },
            {
                title: "提案価格", // Giá đề xuất
                dataIndex: "price",
                key: "price",
                render: (price) => (
                    <Text strong style={{ color: '#B91C1C' }}>
                        {typeof price === 'number' ? `¥${price.toLocaleString()}` : price || "-"}
                    </Text>
                )
            },
            {
                title: "卸価格", // Giá sỉ
                dataIndex: "wholesale_price",
                key: "wholesale_price",
                render: (price) => price ? `¥${Number(price).toLocaleString()}` : "-"
            }
        ];

        const dateColumns: ColumnsType<any> = scheduleDates.map((dateStr) => ({
            title: formatDateDDMM(dateStr),
            key: dateStr,
            align: 'center',
            render: (_, record) => {
                const schedule = record.schedules?.find((s: any) => s.delivery_date === dateStr);
                return schedule ? schedule.quantity : 0;
            }
        }));

        return [...baseColumns, ...dateColumns];
    }, [scheduleDates]);

    return (
        <div style={{ margin: '0 auto', padding: '16px 16px' }}>
            {/* Upper Card Header */}
            <Card
                variant="borderless"
                style={{ borderRadius: 16, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                styles={{ body: { padding: 24 } }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Space align="center" size={12} style={{ marginBottom: 12 }}>
                            <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
                                提案番号: {proposal?.propose_code || "N/A"} {/* Mã đề xuất */}
                            </Title>
                            {proposal?.status && (
                                <Tag color={getStatusConfig(proposal.status).color} icon={getStatusConfig(proposal.status).icon}>
                                    {getStatusConfig(proposal.status).label}
                                </Tag>
                            )}
                        </Space>

                        <Space orientation="vertical" size={4}>
                            <Text style={{ color: '#4B5563', fontSize: 14 }}>
                                有効期間 : {/* Thời gian : */} <Text strong style={{ color: '#111827' }}>{formatDate(proposal?.effective_from)} ~ {formatDate(proposal?.effective_to)}</Text>
                                <span style={{ margin: '0 8px', color: '#D1D5DB' }}>|</span>
                                送信者: {/* Người gửi: */} <Text strong style={{ color: '#111827' }}>{proposal?.recipient_name || "N/A"}</Text>
                            </Text>
                            {proposal?.effective_from && (
                                <Text style={{ color: '#6B7280', fontSize: 14 }}>
                                    出荷予定日 : {formatDate(proposal?.effective_from)} {/* Ngày xuất hàng : */}
                                </Text>
                            )}
                            {proposal?.effective_from && (
                                <Text style={{ color: '#6B7280', fontSize: 14 }}>
                                    納品予定日 : {formatDate(proposal?.effective_from)} {/* Thời gian giao hàng dự kiến : */}
                                </Text>
                            )}
                        </Space>
                    </div>

                    {/* Header Action Buttons */}
                    <Space size={12}>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={onExport}
                            style={{ height: 40, borderRadius: 8, padding: '0 20px', fontWeight: 500 }}
                        >
                            エクスポート {/* export */}
                        </Button>
                        <Button
                            type="primary"
                            onClick={onCreateOrder}
                            style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600, backgroundColor: '#2563EB' }}
                        >
                            この提案から注文を作成 {/* Tạo Order từ đề xuất này */}
                        </Button>
                    </Space>
                </div>
            </Card>

            {/* Table Content Section */}
            <Card
                variant="borderless"
                style={{ borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                styles={{ body: { padding: 24 } }}
            >
                <Title level={5} style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 700, color: '#111827' }}>
                    商品明細および納品予定スケジュール {/* Bảng kê chi tiết sản phẩm & Lịch cung cấp dự kiến */}
                </Title>

                <Table
                    columns={columns}
                    dataSource={proposal?.items || []}
                    rowKey={(record, idx) => record.id || idx}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered={false}
                />
            </Card>
        </div>
    );
};